import { getAuth, updateProfile } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, FormFeedback, FormGroup, FormText, Input, Label, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import '../styles/profile-screen.scss';
import MultipleSpinners from '../components/multiple-spinners'
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllCategories, fetchAllDrawings, fetchColoringHistory, updateUserInfo, uploadImageDataUrl } from '../firebase';
import MasonryDrawings from '../components/masonry-drawings';
import { setMessage, setCrtUser, fetchPostsFirstBatch, fetchAllUsers } from '../store';
import FeedContainer from '../components/feed-container';
import { userPicUploadUrl } from '../utils';

function ProfileScreen() {
  const dispatch = useDispatch()
  const picInput = useRef(null)
  const crtUser = useSelector(state => state.crtUser)
  const [loading, setLoading] = useState(true)
  const [userForm, setUserForm] = useState({ displayName: { value: '' }, email: { value: '' } })
  const [selectedTab, setSelectedTab] = useState('history')
  const [coloringHistory, setColoringHistory] = useState([])
  const [categories, setCategories] = useState([])
  const [historyDrawings, setHistoryDrawings] = useState([])
  const [newPicDataUrl, setNewPicDataUrl] = useState(null)

  const isSaveEnabled = userForm.displayName.valid || newPicDataUrl
  const profilePic = newPicDataUrl || (crtUser && crtUser.photoURL)

  useEffect(() => {
    if (crtUser.uid) {
      setUserForm({
        displayName: {
          value: crtUser.displayName,
          valid: null
        },
        email: {
          value: crtUser.email,
          valid: null
        }
      })

      Promise.all([fetchAllCategories(), fetchAllDrawings(), fetchColoringHistory(crtUser.uid)])
        .then(([categories, allDrawings, history]) => {
          setColoringHistory(history)
          setCategories(categories)

          const historyDrawingsMap = history.reduce((acc, crt) => {
            acc[crt.drawingId] = crt
            return acc
          }, {})

          const historyDrawings = allDrawings.reduce((acc, crt) => {
            if (historyDrawingsMap[crt.id]) {
              acc.push({ ...crt, imageUrl: historyDrawingsMap[crt.id].imageUrl })
            }

            return acc
          }, [])

          setHistoryDrawings(historyDrawings)
        })
        .finally(() => setLoading(false))
    }
  }, [crtUser])

  function handleNameChange(evt) {
    const value = evt.target.value

    setUserForm({
      ...userForm,
      displayName: {
        value: value,
        valid: value !== '',
        invalid: value == ''
      }
    })
  }

  async function handleSave() {
    if (isSaveEnabled) {
      setLoading(true)

      const data = {
        displayName: userForm.displayName.value,
      }

      if (newPicDataUrl) {
        data['uploadedPic'] = true
        data['photoURL'] = await uploadImageDataUrl(userPicUploadUrl(crtUser.uid), newPicDataUrl)
      }

      updateProfile(getAuth().currentUser, data)
        .then(() => {
          updateUserInfo(crtUser.uid, data)
          getAuth().currentUser.reload().then(() => dispatch(setCrtUser(getAuth().currentUser.toJSON())))

          dispatch(setMessage({
            text: 'Your profile has been updated successfully!',
            status: 'success'
          }))
        })
        .catch(() => {
          dispatch(setMessage({
            text: 'Something went wrong while updating your profile. Please try again!',
            status: 'error'
          }))
        })
        .finally(() => setLoading(false))
    }
  }

  function restForm() {
    setUserForm({
      ...userForm,
      displayName: { value: crtUser.displayName }
    })

    setNewPicDataUrl(null)
  }

  function onPicChange(evt) {
    if (!evt.target.files || !evt.target.files[0]) return;

    const reader = new FileReader()

    reader.onloadend = function (e) {
      setNewPicDataUrl(e.target.result)
    }

    reader.readAsDataURL(evt.target.files[0]);
  }

  function triggerSelectPic() {
    if (picInput.current) {
      picInput.current.click()
    }
  }

  function handleSelectMyPosts() {
    setSelectedTab('myposts')
    dispatch(fetchAllUsers())
    dispatch(fetchPostsFirstBatch({ userId: crtUser.uid }))
  }

  return (
    <div id="profile-screen" className="full-screen">
      <div id="wrapper" className={loading ? 'loading' : ''}>
        {loading && <MultipleSpinners />}

        {!loading && <>
          <div className="section left">
            <h5 className='mb-4'>Profile Details</h5>

            <div id="profile-pic-cont">
              <img alt="profile-picture" src={profilePic} />
              <input type="file" ref={picInput} onChange={onPicChange} accept="image/png, image/jpeg" />
              <Button color="light" onClick={triggerSelectPic}><i className="bi bi-camera"></i></Button>
            </div>

            <Form className='mt-4'>
              <FormGroup>
                <Label for="display-name">Display Name</Label>
                <Input id="display-name" {...userForm.displayName} onChange={handleNameChange} />
                <FormText>
                  This is puplic and visible to everyone.
                </FormText>
              </FormGroup>

              <FormGroup>
                <Label for="email">Email</Label>
                <Input id="email" {...userForm.email} disabled readOnly />
                <FormFeedback>
                  You will not be able to see this
                </FormFeedback>
                <FormText>
                  This is private. Your email is never shared with anyone.
                </FormText>
              </FormGroup>
            </Form>

            <div id="form-btns">
              <Button size='sm' onClick={restForm} color="danger">Reset</Button>
              <Button size='sm' onClick={handleSave} color="primary" disabled={!isSaveEnabled}>Save</Button>
            </div>
          </div>

          <div className="section right">
            <Nav tabs>
              <NavItem>
                <NavLink className={selectedTab === 'history' ? "active" : ''} onClick={() => setSelectedTab('history')}>
                  Coloring History
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={selectedTab === 'myposts' ? "active" : ''} onClick={handleSelectMyPosts}>
                  My Posts
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={selectedTab}>
              <TabPane tabId="history">
                {!!historyDrawings.length && <MasonryDrawings drawings={historyDrawings} categories={categories} selectedCatId="all" />}
                {!historyDrawings.length && <p className='text-center pt-3'>You did not save any coloring pages yet!</p>}
              </TabPane>
              <TabPane tabId="myposts">
                <FeedContainer isProfileScreen={true} />
              </TabPane>
            </TabContent>
          </div>
        </>}
      </div>
    </div>
  )
}

export default ProfileScreen;
